import { S as Swiper } from './swiper-core-CZxReCTq.js';
import { P as Pagination } from './pagination-C0tMDIMY.js';
import './create-element-if-not-defined-2GaAVph8.js';

class UpsellCarousel extends HTMLElement {
  async connectedCallback() {
    this.upsellProductsSource = this.dataset.upsellProductsSource;
    this.products = [];
    this.productsWithUpsellMarkup = [];
    this.sectionId = this.dataset.sectionId;
    this.recommendationAPIUrl = this.dataset.recommendationApiUrl;
    this.carouselWrapper = this.querySelector('[data-carousel-wrapper]');

    this.activeProduct = null;
    this.swiper = null;

    this.getProductIdsWithPrice();
    await this.getAllUpsellProducts();
    this.showRelevantUpsellproducts();
    this.initSwiper();

    this.unsubScribeCartAdded = subscribe('cart:added', this.handleCartAdded.bind(this));
    this.unsubscribeCartRemoved = subscribe('cart:change', this.handleCartChange.bind(this));
    // This is used when a product is removed from the cart.
    this.unsubscribeCartUpdated = null;
  }

  disconnectedCallback() {
    if (this.unsubScribeCartAdded) {
      this.unsubScribeCartAdded();
    }
    if (this.unsubscribeCartRemoved) {
      this.unsubscribeCartRemoved();
    }
  }

  getProductIdsWithPrice() {
    if (!this.dataset.productIdsWithPrice) return [];
    this.products = this.dataset.productIdsWithPrice.split(',');
  }

  async getAllUpsellProducts() {
    if (!this.products.length) return;
    const perProductUpsellProducts = [];

    const promises = this.products.map((product) => {
      const id = Number(product.split(';')[0]);
      const price = Number(product.split(';')[1]);
      if (!id || !price) return;

      return this.getProductComplementaryproducts(id).then(({ markup, upsellProductIds }) => {
        if (!markup) return;

        perProductUpsellProducts.push({
          id,
          price,
          html: markup,
          upsellProductIds,
        });
      });
    });

    await Promise.all(promises);
    this.productsWithUpsellMarkup = perProductUpsellProducts.sort((a, b) => b.price - a.price);
  }

  getProductComplementaryproducts(productId) {
    try {
      const response = fetch(
        `${this.recommendationAPIUrl}?section_id=${this.sectionId}&product_id=${productId}&intent=complementary&limit=4`
      )
        .then((response) => response.text())
        .then((text) => {
          const wrapper = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('[data-all-upsell-products]');

          const html = wrapper?.innerHTML || '';
          const upsellProductIds =
            wrapper?.dataset.upsellProductIdsUnfiltered.split(',').map((el) => Number(el)) || [];

          return { markup: html, upsellProductIds };
        });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  showRelevantUpsellproducts() {
    if (!this.productsWithUpsellMarkup.length || !this.carouselWrapper) {
      this.hideParentElement();
      return;
    }
    this.showParentElement();

    // We want to show the upsell products fo the most expensive product.
    const productToShowUpsellFor = this.productsWithUpsellMarkup.find(
      (product) => product.html !== ''
    );

    if (!productToShowUpsellFor) {
      this.hideParentElement();
      return;
    }

    this.activeProduct = {
      id: productToShowUpsellFor.id,
      price: productToShowUpsellFor.price,
      upsellIDs: productToShowUpsellFor.upsellIDs,
    };

    this.carouselWrapper.innerHTML = productToShowUpsellFor.html;
  }

  hideParentElement() {
    const parentElement = this.closest('[data-upsell-carousel-parent-element]');
    if (!parentElement) return;
    parentElement.style.display = 'none';
  }

  showParentElement() {
    const parentElement = this.closest('[data-upsell-carousel-parent-element]');
    if (!parentElement) return;
    parentElement.style.display = 'block';
  }

  initSwiper() {
    const swiperEl = this.querySelector('.swiper');
    if (!swiperEl) return;

    this.swiper = new Swiper(swiperEl, {
      modules: [Pagination],
      spaceBetween: 8,
      slidesPerView: 1.2,
      loop: false,
      breakpointsBase: 'container',
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      breakpoints: {
        450: {
          slidesPerView: 1.8,
        },
        640: {
          slidesPerView: 2.2,
        },
      },
    });
  }

  async handleCartAdded(e) {
    const newProductsUpsellData = [];
    let productsWithThisProductInUpsell = [];

    const promises = e.items.map((item) => {
      const { product_id: id, price } = item;
      this.products.push(`${id};${price}`);
      // Get all products with upsell that have the newly added product in the upsell,
      // so we can later refetch the markup for these products to filter out the newly
      // added product upsell card.
      productsWithThisProductInUpsell = this.productsWithUpsellMarkup.filter((product) =>
        product.upsellProductIds.includes(id)
      );

      return this.getProductComplementaryproducts(id).then(({ markup, upsellProductIds }) => {
        if (!markup) return;
        newProductsUpsellData.push({
          id,
          price,
          html: markup,
          upsellProductIds,
        });
      });
    });

    // For each product we've already fetched, where the newly added cart product is part of the upsell,
    //  we refetch (And remove the old) the data. The liquid handles the filtering out.
    productsWithThisProductInUpsell.forEach((product) => {
      this.productsWithUpsellMarkup = this.productsWithUpsellMarkup.filter(
        (p) => p.id !== product.id
      );
      promises.push(
        this.getProductComplementaryproducts(product.id).then(({ markup, upsellProductIds }) => {
          if (!markup) return;
          newProductsUpsellData.push({
            id: product.id,
            price: product.price,
            html: markup,
            upsellProductIds,
          });
        })
      );
    });

    await Promise.all(promises);

    this.productsWithUpsellMarkup = [
      ...this.productsWithUpsellMarkup,
      ...newProductsUpsellData,
    ].sort((a, b) => b.price - a.price);

    this.showRelevantUpsellproducts();
    this.swiper?.update();
  }

  handleCartChange(e) {
    if (e.quantity !== 0) return;

    this.unsubscribeCartUpdated = subscribe('cart:updated', async () => {
      // If a product is removed, we need to refetch the data of the section since even the initial
      // rendering of this section might not include the currently deleted product in the
      // recommendations (even though its a complementary product). This is because the
      // recommendation API already filters out based on the cart data.
      this.products = this.products.filter((product) => {
        const id = Number(product.split(';')[0]);
        return id !== e.productId;
      });

      await this.getAllUpsellProducts();
      this.showRelevantUpsellproducts();
      this.swiper?.update();

      setTimeout(() => {
        if (this.unsubscribeCartUpdated) this.unsubscribeCartUpdated();
        this.unsubscribeCartUpdated = null;
      }, 1000);
    });
  }
}

customElements.define('upsell-carousel', UpsellCarousel);
